// ============================================================================
//  SpreadingPage — "What's spreading" teacher dashboard.
//  /event/:slug/spreading
//
//  Per the user's brief (May 2026):
//    "Watching memories spread through the school community."
//    NOT analytics. NOT charts. NOT engagement metrics.
//    Teachers care about: "the children are genuinely loving this."
//
//  Design rules:
//    • Headline emotional sentence — NEVER a percentage / score
//      number stares back. (The score IS surfaced via a slim
//      sub-pill so ops can still read it, but it's not the focus.)
//    • Right-now feed = micro-moments, no names, no totals — reads
//      as the room's heartbeat ("A family just watched a moment").
//    • Featured tonight = celebration carousel — re-uses the same
//      data the Venue Projector + Best Spins surface.
//    • Warmest moments = "replayed N times tonight" — strongest
//      proxy for "the children are loving this".
//    • NO leaderboards. NO ranks. NO admin-clutter sidebar.
//
//  EventDrop Originals: matches the dark cinematic universe of
//  Booth360Page / Venue Live — Georgia serif headlines, gold accents,
//  framer-motion slow easings, glass-morphism only on hero card.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import BackButton from '../../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_THEME = {
  quiet:          { color: '#7F8593', glow: 'rgba(127,133,147,0.32)' },
  growing:        { color: '#3FA7FF', glow: 'rgba(63,167,255,0.45)' },
  spreading_fast: { color: '#B89762', glow: 'rgba(184,151,98,0.55)' },
  viral_tonight:  { color: '#D4AF5E', glow: 'rgba(212,175,94,0.6)'  },
};

const CATEGORY_ACCENT = {
  trending:         { color: '#FF6B6B', glow: 'rgba(255,107,107,0.45)' },
  crowd_favourite:  { color: '#D4AF5E', glow: 'rgba(212,175,94,0.45)'  },
  most_replayed:    { color: '#B89762', glow: 'rgba(184,151,98,0.45)'  },
  biggest_reaction: { color: '#FFB84D', glow: 'rgba(255,184,77,0.45)'  },
};

// "5m ago", "Just now" — keep relative + warm. No precise timestamps.
const relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 30) return 'Just now';
  if (s < 60) return 'A moment ago';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return 'Earlier today';
};

const SpreadingPage = () => {
  const { slug } = useParams();
  const [eventDoc, setEventDoc] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Resolve event by slug → event_id (the API takes event_id, slug is
  // the user-facing handle).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (!cancelled) setEventDoc(r.data);
      } catch {
        if (!cancelled) setEventDoc({ event_id: null, event_name: 'Event' });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const eventId = eventDoc?.event_id;

  // Poll the dashboard every 25s — slow enough not to hammer the
  // server, fast enough that a new spin lands inside one teacher
  // attention span. NOT SSE here: the teacher dashboard is a calm
  // surface, not a hyperactive ticker.
  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/spreading`);
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 25000);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  // Tick every 30s so "5m ago" relative timestamps stay fresh without
  // re-fetching the feed.
  useEffect(() => {
    const t = setInterval(() => setTick((k) => k + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (loading || !data) {
    return (
      <div
        data-testid="spreading-page-loading"
        style={{
          minHeight: '100svh',
          background: 'radial-gradient(circle at 50% 30%, #14121f 0%, #050307 70%)',
          color: 'rgba(255,255,255,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 18, letterSpacing: '0.18em', textTransform: 'uppercase',
        }}
      >
        ✦ Listening…
      </div>
    );
  }

  const theme = STATUS_THEME[data.headline.status_slug] || STATUS_THEME.quiet;

  return (
    <div
      data-testid="spreading-page"
      style={{
        minHeight: '100svh',
        background: 'radial-gradient(circle at 50% 0%, #14121f 0%, #050307 70%)',
        color: '#FFFFFF',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        padding: '32px 22px 64px',
      }}
    >
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <BackButton fallbackHref={slug ? `/event/${slug}` : '/dashboard'} label="Back" variant="subtle" testId="spreading-back-btn" />
        </div>
        {/* ── Eyebrow ── */}
        <div
          data-testid="spreading-eyebrow"
          style={{
            fontSize: 12, fontWeight: 700, letterSpacing: '0.32em',
            textTransform: 'uppercase', color: 'rgba(212,175,94,0.78)',
            marginBottom: 12,
          }}
        >
          ✦ {eventDoc?.event_name || 'Event'} · Live tonight
        </div>

        {/* ── Hero narrative ──
            The single sentence. NO score in the headline. Score sits
            below as a slim pill so ops can still read the number
            without it dominating the page. */}
        <motion.h1
          data-testid="spreading-headline"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          style={{
            margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(36px, 6vw, 56px)', fontWeight: 400,
            letterSpacing: '-0.025em', lineHeight: 1.1,
            textShadow: `0 0 60px ${theme.glow}`,
          }}
        >
          {data.headline.narrative}
        </motion.h1>

        {/* ── Sub-pill: status + soft community counts ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', gap: 10 }}
        >
          <span
            data-testid="spreading-status-pill"
            style={{
              padding: '8px 16px', borderRadius: 999,
              background: `${theme.color}1f`,
              border: `1px solid ${theme.color}66`,
              boxShadow: `0 0 18px ${theme.glow}`,
              color: theme.color, fontSize: 13, fontWeight: 800,
              letterSpacing: '0.04em',
            }}
          >
            {data.headline.status_label}
          </span>
          {data.headline.families_watching > 0 && (
            <span
              data-testid="spreading-families-pill"
              style={{
                padding: '8px 16px', borderRadius: 999,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
              }}
            >
              🏡 Watching from {data.headline.families_watching} {data.headline.families_watching === 1 ? 'home' : 'homes'}
            </span>
          )}
          {data.headline.moments_shared > 0 && (
            <span
              data-testid="spreading-moments-pill"
              style={{
                padding: '8px 16px', borderRadius: 999,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
              }}
            >
              📱 Shared {data.headline.moments_shared} {data.headline.moments_shared === 1 ? 'time' : 'times'}
            </span>
          )}
        </motion.div>

        {/* ── Right now feed ── */}
        <section
          data-testid="spreading-right-now"
          style={{
            marginTop: 40, padding: 22, borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(20,18,32,0.92) 0%, rgba(32,20,40,0.92) 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: `0 0 26px ${theme.glow}`,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14,
          }}>
            <h2 style={{
              margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em',
            }}>
              Right now
            </h2>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
            }}>
              ✦ heartbeat
            </span>
          </div>

          {data.right_now.length === 0 ? (
            <p
              data-testid="spreading-right-now-empty"
              style={{
                margin: 0, padding: '20px 0',
                fontSize: 15, color: 'rgba(255,255,255,0.5)',
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
              }}
            >
              The room is quiet — the next moment will land here.
            </p>
          ) : (
            <ol
              style={{ listStyle: 'none', padding: 0, margin: 0,
                       display: 'flex', flexDirection: 'column', gap: 2 }}
              key={`feed-${tick}`}
            >
              <AnimatePresence initial={false}>
                {data.right_now.map((m, i) => (
                  <motion.li
                    key={`${m.ts}-${i}`}
                    data-testid={`spreading-moment-${i}`}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: 'easeOut' }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '13px 4px',
                      borderBottom: i === data.right_now.length - 1
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <span style={{
                      fontSize: 22, lineHeight: 1, width: 28,
                      textAlign: 'center', flexShrink: 0,
                    }}>{m.icon}</span>
                    <span style={{
                      flex: 1,
                      fontSize: 15, fontWeight: 500,
                      color: 'rgba(255,255,255,0.86)', letterSpacing: '-0.005em',
                    }}>
                      {m.text}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)',
                      whiteSpace: 'nowrap',
                    }}>
                      {relativeTime(m.ts)}
                    </span>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ol>
          )}
        </section>

        {/* ── Featured tonight ── */}
        {data.featured_tonight && data.featured_tonight.length > 0 && (
          <section
            data-testid="spreading-featured-tonight"
            style={{
              marginTop: 26, padding: 22, borderRadius: 20,
              background: 'linear-gradient(135deg, rgba(20,18,32,0.92) 0%, rgba(32,20,40,0.92) 100%)',
              border: '1px solid rgba(212,175,94,0.22)',
              boxShadow: '0 0 28px rgba(212,175,94,0.18)',
            }}
          >
            <div style={{ marginBottom: 14, display: 'flex',
                          alignItems: 'baseline', gap: 12 }}>
              <h2 style={{
                margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em',
              }}>
                Featured tonight
              </h2>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.32em',
                textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)',
              }}>
                ✦ on the live wall
              </span>
            </div>
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            }}>
              {data.featured_tonight.map((cat) => {
                const accent = CATEGORY_ACCENT[cat.category_slug]
                            || CATEGORY_ACCENT.trending;
                return (
                  <a
                    key={cat.category_slug}
                    data-testid={`spreading-featured-${cat.category_slug}`}
                    href={cat.video_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 8,
                      borderRadius: 14, padding: 10,
                      background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${accent.color}3a`,
                      boxShadow: `0 4px 18px ${accent.glow}`,
                      textDecoration: 'none', color: 'inherit',
                    }}
                  >
                    <div style={{
                      width: '100%', aspectRatio: '9 / 16', maxHeight: 200,
                      borderRadius: 10, position: 'relative', overflow: 'hidden',
                      background: cat.thumb_url
                        ? `url(${cat.thumb_url}) center/cover no-repeat`
                        : 'linear-gradient(135deg, #1a1626, #2d1f3e)',
                    }}>
                      <div style={{
                        position: 'absolute', top: 6, left: 6,
                        padding: '3px 8px', borderRadius: 999,
                        background: `${accent.color}d0`,
                        color: '#0F0F14', fontSize: 9, fontWeight: 800,
                        letterSpacing: '0.04em',
                        boxShadow: `0 0 12px ${accent.glow}`,
                      }}>
                        {cat.category_icon} {cat.category_label}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Warmest moments ── */}
        {data.warmest_moments && data.warmest_moments.length > 0 && (
          <section
            data-testid="spreading-warmest"
            style={{
              marginTop: 26, padding: 22, borderRadius: 20,
              background: 'linear-gradient(135deg, rgba(20,18,32,0.92) 0%, rgba(28,20,32,0.92) 100%)',
              border: '1px solid rgba(255,184,77,0.22)',
            }}
          >
            <div style={{ marginBottom: 14 }}>
              <h2 style={{
                margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em',
              }}>
                Moments the room keeps coming back to
              </h2>
              <p style={{
                margin: '6px 0 0', fontSize: 13, fontWeight: 500,
                color: 'rgba(255,255,255,0.55)', fontStyle: 'italic',
                fontFamily: 'Georgia, serif',
              }}>
                These keep getting replayed — a quiet sign people are loving them.
              </p>
            </div>
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            }}>
              {data.warmest_moments.map((w) => (
                <a
                  key={w.booth_id}
                  data-testid={`spreading-warmest-${w.booth_id}`}
                  href={w.video_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 8,
                    borderRadius: 14, padding: 10,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,184,77,0.32)',
                    boxShadow: '0 4px 18px rgba(255,184,77,0.18)',
                    textDecoration: 'none', color: 'inherit',
                  }}
                >
                  <div style={{
                    width: '100%', aspectRatio: '9 / 16', maxHeight: 200,
                    borderRadius: 10, position: 'relative', overflow: 'hidden',
                    background: w.thumb_url
                      ? `url(${w.thumb_url}) center/cover no-repeat`
                      : 'linear-gradient(135deg, #1a1626, #2d1f3e)',
                  }} />
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    color: 'rgba(255,184,77,0.92)',
                    letterSpacing: '0.04em', textAlign: 'center',
                  }}>
                    {w.narrative}
                  </span>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* ── Footer brand ── */}
        <p style={{
          marginTop: 48, textAlign: 'center',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: 'rgba(212,175,94,0.45)',
        }}>
          ✦ Made with EventDrop
        </p>
      </div>
    </div>
  );
};

export default SpreadingPage;
